import { useTranslation } from "@sagot/i18n";
import { useMemo } from "react";
import { z } from "zod";
import type { Action } from "../shared/types";

/** Limits mirror backend `app/core/constants/bookings.py` (booking form). */
export const FORM_LIMITS = { fields: 15, options: 30, label: 80, option: 80 } as const;
export const FORM_FIELD_TYPES = ["text", "textarea", "select", "checkboxes", "email", "tel"] as const;
export type FormFieldType = (typeof FORM_FIELD_TYPES)[number];
const CHOICE_TYPES: ReadonlySet<FormFieldType> = new Set(["select", "checkboxes"]);
/** Keys the built-in booking fields, consent and slot picker already use (backend `FORM_RESERVED_KEYS`). */
const RESERVED_KEYS: ReadonlySet<string> = new Set(["name", "email", "mobile", "notes", "party_size", "consent", "slot_id", "date", "time"]);
const KEY_MAX_CHARS = 40;
const KEY_FALLBACK = "field";
const OPTION_LINE = /\r?\n/;

export const hasChoices = (type: FormFieldType): boolean => CHOICE_TYPES.has(type);

export interface FormFieldValues {
  /** Stable answer key; empty for a field added in this session (derived from the question on save). */
  key: string;
  label: string;
  type: FormFieldType;
  required: boolean;
  /** Choices, one per line (dropdown and checkboxes only). */
  optionsText: string;
}

export interface BookingFormValues {
  ask_phone: boolean;
  ask_notes: boolean;
  fields: FormFieldValues[];
}

export interface BookingFormPayload {
  ask_phone: boolean;
  ask_notes: boolean;
  fields: { key: string; label: string; type: FormFieldType; required: boolean; options: string[] }[];
}

const storedField = z.object({
  key: z.string(),
  label: z.string(),
  type: z.enum(FORM_FIELD_TYPES).catch("text"),
  required: z.boolean().catch(false),
  options: z.array(z.string()).catch([]),
});
const storedForm = z.object({
  ask_phone: z.boolean().catch(true),
  ask_notes: z.boolean().catch(true),
  fields: z.array(storedField).catch([]),
});

export const EMPTY_FIELD: FormFieldValues = { key: "", label: "", type: "text", required: false, optionsText: "" };

/** `ActionOut.booking_form` → editor values (the default form when absent). */
export function bookingFormFromAction(action: Pick<Action, "booking_form">): BookingFormValues {
  const parsed = storedForm.safeParse(action.booking_form ?? {});
  const form = parsed.success ? parsed.data : { ask_phone: true, ask_notes: true, fields: [] };
  return {
    ask_phone: form.ask_phone,
    ask_notes: form.ask_notes,
    fields: form.fields.map((field) => ({ key: field.key, label: field.label, type: field.type, required: field.required, optionsText: field.options.join("\n") })),
  };
}

export function optionsOf(text: string): string[] {
  return text.split(OPTION_LINE).map((line) => line.trim()).filter(Boolean);
}

/** "Current HR system?" → `current_hr_system`, unique among `taken` and never a reserved key. */
export function keyFromLabel(label: string, taken: ReadonlySet<string>): string {
  const slug = label.toLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, "_").replace(/^[^a-z]+|_+$/g, "").slice(0, KEY_MAX_CHARS - 3) || KEY_FALLBACK;
  let key = slug;
  for (let n = 2; RESERVED_KEYS.has(key) || taken.has(key); n += 1) key = `${slug}_${String(n)}`;
  return key;
}

/** Editor values → `ActionUpdate.booking_form`; new fields get keys from their question. */
export function toBookingForm(values: BookingFormValues): BookingFormPayload {
  const taken = new Set(values.fields.map((field) => field.key).filter(Boolean));
  const fields = values.fields.map((field) => {
    const key = field.key || keyFromLabel(field.label, taken);
    taken.add(key);
    return { key, label: field.label.trim(), type: field.type, required: field.required, options: hasChoices(field.type) ? optionsOf(field.optionsText) : [] };
  });
  return { ask_phone: values.ask_phone, ask_notes: values.ask_notes, fields };
}

const P = "build.actions.bookingForm.errors";

/** zod schema with translated messages (same rules as the backend `BookingForm`). */
export function useBookingFormSchema() {
  const { t } = useTranslation();
  return useMemo(() => {
    const field = z
      .object({ key: z.string(), label: z.string().trim().min(1, t(`${P}.label`)).max(FORM_LIMITS.label, t(`${P}.labelMax`, { count: FORM_LIMITS.label })), type: z.enum(FORM_FIELD_TYPES), required: z.boolean(), optionsText: z.string() })
      .superRefine((value, ctx) => {
        if (!hasChoices(value.type)) return;
        const options = optionsOf(value.optionsText);
        const issue = (message: string) => { ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["optionsText"], message }); };
        if (options.length === 0) issue(t(`${P}.options`));
        else if (options.length > FORM_LIMITS.options) issue(t(`${P}.optionsMax`, { count: FORM_LIMITS.options }));
        else if (options.some((option) => option.length > FORM_LIMITS.option)) issue(t(`${P}.optionMax`, { count: FORM_LIMITS.option }));
        else if (new Set(options).size !== options.length) issue(t(`${P}.duplicateOption`));
      });
    return z.object({ ask_phone: z.boolean(), ask_notes: z.boolean(), fields: z.array(field).max(FORM_LIMITS.fields, t(`${P}.tooMany`, { count: FORM_LIMITS.fields })) });
  }, [t]);
}
